<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @ok="duplicateArticle"
        >
            <template #modal-title>
                Duplicate Article
            </template>

            <div v-if="loading_publications" class="mb-3">
                <b-spinner label="Loading..."></b-spinner>
            </div>

            <div v-else>
                <b-form-group
                    label="Publication"
                    label-for="target_publication"
                >
                    <multiselect
                        v-model="target_publication"
                        :options="filtered_publications"
                        :class="{'is-invalid': validating && !target_publication}"
                        label="sku"
                        track-by="sku"
                        :internal-search="false"
                        @search-change="filterPublications"
                        @select="publicationChanged"
                    >
                        <template
                            v-for="slotName in ['option', 'singleLabel']"
                            #[slotName]="{ option }"
                        >
                            <div
                                :key="slotName"
                                :class="slotName === 'option' ? 'option__desc' : null"
                            >
                                <span :class="slotName === 'option' ? 'option__title' : null">
                                    <span v-if="option.title">{{ option.title }}</span>
                                    <span v-else>no title</span>
                                    <span>({{ option.sku }})</span>
                                </span>
                            </div>
                        </template>
                    </multiselect>

                    <b-form-text>
                        Select the Publication you would like to duplicate this article into
                    </b-form-text>

                    <b-form-invalid-feedback :state="validating && !target_publication ? false : null">
                        Publication is required
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                    label-for="target_unit"
                >
                    <label>
                        Unit <span v-if="unit_groups" :class="{'text-success': unit_groups.length > 0, 'text-danger': !unit_groups || unit_groups && !unit_groups.length}">({{unit_groups.length}})</span>
                    </label>
                    <multiselect
                        v-model="target_unit_group"
                        :options="unit_groups"
                        :class="{'is-invalid': validating && !target_unit_group}"
                        label="title"
                        track-by="title"
                    >
                        <template
                            v-for="slotName in ['option', 'singleLabel']"
                            #[slotName]="{ option }"
                        >
                            <div
                                :key="slotName"
                                :class="slotName === 'option' ? 'option__desc' : null"
                            >
                                <span :class="slotName === 'option' ? 'option__title' : null">
                                    <span v-if="option.title">{{ option.title }}</span>
                                    <span v-else>no title</span>
                                </span>
                            </div>
                        </template>
                    </multiselect>

                    <b-form-text>
                        Select the Unit you would like to duplicate this article into
                    </b-form-text>

                    <b-form-invalid-feedback :state="validating && !target_publication ? false : null">
                        Unit is required
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                    v-if="target_unit_group"
                    label-for="target_unit"
                >
                    <label>
                        Week <span v-if="units" :class="{'text-success': units.length > 0, 'text-danger': !units || units && !units.length}">({{units.length}})</span>
                    </label>
                    <multiselect
                        v-model="target_unit"
                        :options="units"
                        :class="{'is-invalid': validating && !target_unit}"
                        label="title"
                        track-by="title"
                        :disabled="!target_unit_group"
                    >
                        <template
                            v-for="slotName in ['option', 'singleLabel']"
                            #[slotName]="{ option }"
                        >
                            <div
                                :key="slotName"
                                :class="slotName === 'option' ? 'option__desc' : null"
                            >
                                <span
                                    v-if="slotName === 'option' && option.order_number"
                                    class="option__title mr-1"
                                >
                                    <span>{{ option.order_number }}.</span>
                                </span>
                                <span :class="slotName === 'option' ? 'option__title' : null">
                                    <span v-if="option.title">{{ option.title }}</span>
                                    <span v-else>no title</span>
                                </span>
                            </div>
                        </template>
                    </multiselect>

                    <b-form-text>
                        <div v-if="target_unit_group">Select the Week you would like to duplicate this article into</div>
                        <div v-else class="text-danger">First select the Unit you would like to duplicate this article into</div>
                    </b-form-text>

                    <b-form-invalid-feedback :state="validating && !target_unit ? false : null">
                        Week is required
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group
                    v-if="target_unit"
                    label-for="target_unit"
                >
                    <label>
                        Page
                    </label>

                    <multiselect
                        v-model="target_page"
                        :options="unit_layout"
                        :class="{'is-invalid': validating && !target_page}"
                        label="page"
                        track-by="page"
                        :disabled="!target_unit"
                    >
                    </multiselect>

                    <b-form-text>
                        Select the page you would like to duplicate this article into
                    </b-form-text>

                    <b-form-invalid-feedback :state="validating && !target_page ? false : null">
                        Page is required
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>

            <template #modal-footer>
                <b-button
                    variant="light"
                    @click="cancel"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="info"
                    @click="duplicateArticle"
                >
                    <font-awesome-icon :icon="['fad', 'clone']" fixed-width /> Duplicate Article
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import { mapState } from 'vuex'

import unit_layout from '@/common/unit_layout'

export default {
    data: () => ({
        unit_groups: [],
        units: [],

        unit_layout: unit_layout,

        target_publication: null,
        target_unit_group: null,
        target_unit: null,
        target_page: null,

        search_term: '',

        loading_publications: false
    }),
    props: [
        'value',
        'unit'
    ],
    components: {

    },
    computed: {
        ...mapState({
            validating: state => state.utilities.validating,
            publications: state => state.publications.publications,
            publication: state => state.publications.publication[0],
        }),
        modal_id () {
            let modal_id = 'duplicate-article-modal'

            return modal_id
        },
        filtered_publications () {
            return this.publications.filter(publication => {
                let search = this.search_term.toLowerCase()

                let search_match = false
                if(
                    this.search_term === '' ||
                    publication.title.toLowerCase().indexOf(search) > -1 ||
                    publication.sku.toLowerCase().indexOf(search) > -1
                ) {
                    search_match = true
                }

                return search_match
            })
        }
    },
    watch: {
        value: {
            handler (value) {
                if (value == true) {
                    this.getPublications()

                    this.$store.commit('setValidating', false)

                    if (this.publication && !this.publication.locked) {
                        this.target_publication = this.publication

                        this.axios.get('/api/unit_groups/' + this.publication.id)
                        .then(response => {
                            _.each(response.data, publication => {
                                if(publication.id == this.target_publication.id) {
                                    this.unit_groups = publication.unit_groups
                                }
                            })
                        })
                    }
                }
            }
        },
        target_publication: {
            handler (publication) {
                if (publication) {
                    this.axios.get('/api/unit_groups/' + publication.id)
                    .then(response => {
                        _.each(response.data, publication => {
                            if(publication.id == this.target_publication.id) {
                                this.unit_groups = publication.unit_groups
                            }
                        })
                    })
                } else {
                    this.unit_groups = []
                }
            }
        },
        target_unit_group: {
            handler (unit_group) {
                if (unit_group) {
                    this.units = unit_group.units
                }
            }
        }
    },
    methods: {
        getPublications () {
            this.loading_publications = true

            this.$store.dispatch('getPublications')
            .then(() => {
                this.loading_publications = false
            })
        },
        filterPublications (search_term) {
            if (search_term) {
                this.search_term = search_term
            }
        },
        duplicateArticle () {
            this.$store.commit('setValidating', true)

            if (!this.validated()) {
                return false
            }

            this.$emit('duplicate', this.target_publication, this.target_unit, this.target_page.page)
        },
        validated () {
            let validated = true

            if (!this.target_publication) {
                validated = false
            }

            else if (!this.target_unit_group) {
                validated = false
            }

            else if (!this.target_unit) {
                validated = false
            }

            else if (!this.target_page) {
                validated = false
            }

            return validated
        },
        publicationChanged () {
            this.$store.commit('setValidating', false)

            this.target_unit_group = null
            this.target_unit = null
            this.target_page = null
        },
        cancel (event) {
            event.preventDefault()

            this.hide()
        },
        hide () {
            this.$emit('hide')
        },
        hidden () {
            this.$store.commit('setValidating', false)

            this.target_publication = null
            this.target_unit_group = null
            this.target_unit = null
            this.target_page = null

            this.$emit('hide')
        }
    }
}
</script>
